import { createRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Route as TenantLayoutRoute } from "./_layout";
import { Button } from "@/components/base/buttons/button";
import { Card, CardBody, CardHeader } from "@/components/application/card/card";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { InputBase } from "@/components/base/input/input";
// useToast zatím bez nativního ekvivalentu v scope guidu → zůstává legacy barrel.
import { useToast } from "@/components/application/notifications/toast-provider";
import {
  microgroupInviteSchema,
  MICROGROUP_RELATIONSHIPS,
  MICROGROUP_RELATIONSHIP_LABELS,
  type MicrogroupInviteInput,
} from "../../schemas/microgroupInvite";
import {
  useMicrogroupInvite,
  useMicrogroupOverview,
} from "../../hooks/useMicrogroupQueries";
import { ApiError } from "../../lib/apiClient";
import { useAuthStore } from "../../lib/authStore";

/**
 * UC-006 Mikroskupina invite Form — React Hook Form + Zod validation.
 *
 * Pole: invited_name (min 2), invited_email (RFC 5322), license_plate (SPZ
 * regex), relationship (enum family/friend/employee/other).
 *
 * Po úspěšném submitu: success Toast "Žádost odeslána Petrovi ke schválení"
 * (per ANALYZE §2.6 Cesta A — admin approval) + redirect zpět na
 * `/tenant/microgroup`.
 */

export const Route = createRoute({
  getParentRoute: () => TenantLayoutRoute,
  path: "/tenant/microgroup/invite",
  component: MicrogroupInviteComponent,
});

function MicrogroupInviteComponent() {
  const auth = useAuthStore((s) => s.auth);
  const navigate = useNavigate();
  const toast = useToast();
  const overview = useMicrogroupOverview();
  const inviteMutation = useMicrogroupInvite(overview.data?.microgroup_id);

  const role = auth.state === "authenticated" ? auth.user.role : "endUser";
  const isSubAdmin = role === "subAdmin" || role === "tenantAdmin";

  const form = useForm<MicrogroupInviteInput>({
    resolver: zodResolver(microgroupInviteSchema),
    defaultValues: {
      name: "",
      email: "",
      relationship: "friend",
    },
    mode: "onBlur",
  });

  if (!isSubAdmin) {
    return (
      <main className="ui-route-main">
        <InlineAlert variant="error" title="Přístup odepřen">
          Pouze sub-admin mikroskupiny smí pozvat nového člena.
        </InlineAlert>
      </main>
    );
  }

  function handleSubmit(values: MicrogroupInviteInput) {
    inviteMutation.mutate(values, {
      onSuccess: () => {
        toast.push({
          variant: "success",
          message: "Žádost odeslána Petrovi ke schválení.",
        });
        navigate({ to: "/tenant/microgroup" });
      },
      onError: (err) => {
        if (err instanceof ApiError && err.errorCode === "DUPLICATE_INVITATION") {
          form.setError("email", {
            message: "Tento e-mail už byl pozván nebo je členem.",
          });
        }
      },
    });
  }

  const serverError =
    inviteMutation.isError && inviteMutation.error instanceof ApiError
      ? inviteMutation.error.message
      : null;

  return (
    <main className="ui-route-main" aria-labelledby="invite-heading">
      <header className="ui-route-header">
        <h1 id="invite-heading">Pozvat nového člena</h1>
        <Button
          color="secondary"
          href="/tenant/microgroup"
          data-testid="microgroup-invite-cancel"
        >
          Zpět
        </Button>
      </header>

      <Card>
        <CardHeader title="Údaje pozvaného" />
        <CardBody>
          <p className="ui-route-lead">
            Po odeslání čeká pozvánka na schválení od Petra (admin). Pozvaný
            obdrží e-mail s odkazem k aktivaci (platnost 14 dní).
          </p>
          {serverError && (
            <InlineAlert variant="error" title="Pozvánka se nepodařila">
              {serverError}
            </InlineAlert>
          )}
          <form
            noValidate
            onSubmit={form.handleSubmit(handleSubmit)}
            className="ui-form"
          >
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-primary" htmlFor="invite-name">
                Jméno a příjmení <span aria-hidden="true">*</span>
              </label>
              <InputBase
                id="invite-name"
                type="text"
                autoComplete="name"
                aria-describedby={form.formState.errors.name ? "invite-name-error" : undefined}
                isInvalid={Boolean(form.formState.errors.name)}
                {...form.register("name")}
                data-testid="microgroup-invite-name"
              />
              {form.formState.errors.name?.message && (
                <p id="invite-name-error" role="alert" className="text-sm text-error-primary">
                  {form.formState.errors.name.message}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-primary" htmlFor="invite-email">
                E-mail <span aria-hidden="true">*</span>
              </label>
              <InputBase
                id="invite-email"
                type="email"
                autoComplete="email"
                aria-describedby={form.formState.errors.email ? "invite-email-error" : undefined}
                isInvalid={Boolean(form.formState.errors.email)}
                {...form.register("email")}
                data-testid="microgroup-invite-email"
              />
              {form.formState.errors.email?.message && (
                <p id="invite-email-error" role="alert" className="text-sm text-error-primary">
                  {form.formState.errors.email.message}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-primary" htmlFor="invite-relationship">
                Vztah k mikroskupině <span aria-hidden="true">*</span>
              </label>
              <select
                id="invite-relationship"
                className="block w-full min-h-touch rounded-lg bg-primary px-3 py-2 text-md text-primary ring-1 ring-inset ring-primary outline-focus-ring focus:ring-2 focus:ring-brand disabled:opacity-50"
                aria-describedby={
                  form.formState.errors.relationship ? "invite-relationship-error" : undefined
                }
                aria-invalid={Boolean(form.formState.errors.relationship) || undefined}
                {...form.register("relationship")}
                data-testid="microgroup-invite-relationship"
              >
                {MICROGROUP_RELATIONSHIPS.map((r) => (
                  <option key={r} value={r}>
                    {MICROGROUP_RELATIONSHIP_LABELS[r]}
                  </option>
                ))}
              </select>
              {form.formState.errors.relationship?.message && (
                <p id="invite-relationship-error" role="alert" className="text-sm text-error-primary">
                  {form.formState.errors.relationship.message}
                </p>
              )}
            </div>
            <div className="ui-form-actions">
              <Button
                type="submit"
                color="primary"
                isLoading={inviteMutation.isPending}
                isDisabled={inviteMutation.isPending}
                data-testid="microgroup-invite-submit"
              >
                Odeslat pozvánku
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </main>
  );
}
